import { CheckCircle, Error, HourglassEmpty, Info, PlayArrow, Refresh, Stop, Warning } from "@mui/icons-material";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import { Datagrid, FunctionField, useRecordContext, useTranslate } from "react-admin";

import { IntegrationExecution } from "../generated/graphql";
import { space } from "../theme/designTokens";
import { ExecutionDetailsModal } from "./ExecutionDetailsModal";
import { LocalizedDateField } from "./fields/LocalizedDateField";

// Custom field components for the Datagrid
const StatusField = ({ getStatusIcon }: { getStatusIcon: (status: string) => JSX.Element }) => {
  const record = useRecordContext<IntegrationExecution>();
  const translate = useTranslate();

  if (!record) return null;

  const isActive = record.status === "pending" || record.status === "running";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {getStatusIcon(record.status)}
      <Typography
        variant="body2"
        sx={{
          textTransform: "capitalize",
          fontWeight: isActive ? "medium" : "normal",
        }}
      >
        {translate(`integration.status.${record.status}`)}
      </Typography>
    </Box>
  );
};

StatusField.defaultProps = { label: "Status" };

const ResultField = ({
  getResultChip,
}: {
  getResultChip: (result: "success" | "warning" | "error") => JSX.Element | null;
}) => {
  const record = useRecordContext<IntegrationExecution>();

  if (!record?.result) return null;

  return getResultChip(record.result as "success" | "warning" | "error");
};

ResultField.defaultProps = { label: "Result" };

interface IntegrationExecutionsTableProps {
  executions: IntegrationExecution[];
  executionsLoading: boolean;
  onRefresh: () => void;
}

export const IntegrationExecutionsTable: React.FC<IntegrationExecutionsTableProps> = ({
  executions,
  executionsLoading,
  onRefresh,
}) => {
  const [selectedExecution, setSelectedExecution] = useState<IntegrationExecution | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const translate = useTranslate();

  /**
   * Format duration helper function
   */
  const formatDuration = (seconds?: number | null) => {
    if (seconds === undefined || seconds === null) return "-";
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  /**
   * Get status icon for a given status
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <HourglassEmpty sx={{ color: "grey.500" }} />;
      case "running":
        return <PlayArrow sx={{ color: "info.main" }} />;
      case "completed":
        return <CheckCircle sx={{ color: "success.main" }} />;
      case "failed":
        return <Error sx={{ color: "error.main" }} />;
      case "cancelled":
        return <Stop sx={{ color: "grey.500" }} />;
      default:
        return <Info sx={{ color: "grey.500" }} />;
    }
  };

  /**
   * Get result chip for a given result
   */
  const getResultChip = (result: "success" | "warning" | "error") => {
    const config = {
      success: { label: translate("integration.result.success"), color: "success" as const, icon: <CheckCircle /> },
      warning: { label: translate("integration.result.warning"), color: "warning" as const, icon: <Warning /> },
      error: { label: translate("integration.result.error"), color: "error" as const, icon: <Error /> },
    };

    const resultConfig = config[result];
    if (!resultConfig) return null;

    return (
      <Chip
        label={resultConfig.label}
        color={resultConfig.color}
        size="small"
        icon={resultConfig.icon}
        variant="outlined"
      />
    );
  };

  /**
   * Open execution details modal
   */
  const handleExecutionDetails = (execution: IntegrationExecution) => {
    setSelectedExecution(execution);
    setDetailsModalOpen(true);
  };

  return (
    <>
      {/* Execution History */}
      <Box sx={{ mt: space.lg }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: space.sm }}>
          <Typography variant="h6">{translate("integration.execution.title")}</Typography>
          <IconButton onClick={onRefresh} disabled={executionsLoading} size="small">
            <Refresh />
          </IconButton>
        </Box>

        {executionsLoading ? (
          <Typography>{translate("integration.execution.loading")}</Typography>
        ) : executions.length === 0 ? (
          <Typography color="text.secondary">{translate("integration.execution.empty")}</Typography>
        ) : (
          <Datagrid
            data={executions}
            bulkActionButtons={false}
            rowClick={false}
            resource="integrationExecution"
            isLoading={executionsLoading}
          >
            <StatusField label={translate("integration.execution.table.status")} getStatusIcon={getStatusIcon} />
            <ResultField label={translate("integration.execution.table.result")} getResultChip={getResultChip} />
            <LocalizedDateField
              source="startedAt"
              label={translate("integration.execution.table.started")}
              showTime
              sortable={false}
            />
            <FunctionField
              label={translate("integration.execution.table.duration")}
              render={(record: IntegrationExecution) => (
                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                  {formatDuration(record.duration)}
                </Typography>
              )}
              sortable={false}
            />
            <FunctionField
              label=""
              render={(record: IntegrationExecution) => (
                <IconButton size="small" onClick={() => handleExecutionDetails(record)}>
                  <Info />
                </IconButton>
              )}
              sortable={false}
            />
          </Datagrid>
        )}
      </Box>
      {selectedExecution && (
        <ExecutionDetailsModal
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          execution={selectedExecution}
        />
      )}
    </>
  );
};
